// frontend/src/pages/Monitoramento.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../api';

const COR = {
  navy: '#1A2233', orange: '#D85A30', surface: '#ffffff',
  border: '#d4dde8', text: '#1A2233', verde: '#16a34a',
  vermelho: '#dc2626', amarelo: '#f59e0b', cinza: '#9ca3af',
};

// ─── Helpers de status ────────────────────────────────────
function getPillProps(ativo, statusAtual) {
  if (!ativo) return { label: 'Pausado', bg: '#fef9c3', cor: '#92400e', border: '#fde68a', dotBg: '#d97706', glow: false };
  if (statusAtual === 'erro') return { label: 'Com erro', bg: '#fee2e2', cor: '#dc2626', border: '#fca5a5', dotBg: '#dc2626', glow: true };
  if (statusAtual === 'ok') return { label: 'Rodando', bg: '#f0fdf4', cor: '#16a34a', border: '#bbf7d0', dotBg: '#16a34a', glow: true };
  return { label: 'Aguardando', bg: '#f3f4f6', cor: '#6b7a8d', border: '#d1d5db', dotBg: '#9ca3af', glow: false };
}

function StatusPill({ ativo, statusAtual }) {
  const p = getPillProps(ativo, statusAtual);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: p.bg, color: p.cor, border: `1.5px solid ${p.border}`,
      borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%', background: p.dotBg, flexShrink: 0,
        boxShadow: p.glow ? `0 0 5px ${p.dotBg}99` : 'none',
      }} />
      {p.label}
    </span>
  );
}

// ─── Sparkline 24h ────────────────────────────────────────
function buildTimeline(historico, data) {
  const porHora = {};
  historico.forEach(h => {
    const hora = new Date(h.criado_em).getHours();
    if (!porHora[hora]) porHora[hora] = [];
    porHora[hora].push(h);
  });
  const agora = new Date();
  const isHoje = data === agora.toISOString().slice(0, 10);
  const horaAtual = agora.getHours();
  return Array.from({ length: 24 }, (_, i) => {
    const itens = porHora[i] || [];
    if (i > horaAtual && isHoje) return { hora: i, status: 'futuro', tempo: null };
    if (itens.length === 0) return { hora: i, status: 'vazio', tempo: null };
    const erros = itens.filter(x => x.status === 'erro').length;
    const okItems = itens.filter(x => x.tempo_ms);
    const tempo = okItems.length ? Math.round(okItems.reduce((a, b) => a + b.tempo_ms, 0) / okItems.length) : null;
    return {
      hora: i,
      status: erros === itens.length ? 'erro' : erros > 0 ? 'parcial' : 'ok',
      tempo,
    };
  });
}

function Sparkline({ slots }) {
  const maxTempo = Math.max(...slots.map(s => s.tempo || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 28, width: '100%' }}>
      {slots.map((s, i) => {
        let bg, h;
        if (s.status === 'futuro') { bg = '#e5e7eb'; h = 3; }
        else if (s.status === 'erro')   { bg = COR.vermelho; h = '100%'; }
        else if (s.status === 'parcial') { bg = COR.amarelo; h = '60%'; }
        else if (s.status === 'vazio')  { bg = '#e5e7eb'; h = 3; }
        else { h = Math.max(Math.round((s.tempo / maxTempo) * 100), 10) + '%'; bg = COR.verde; }
        return (
          <span key={i}
            title={
              s.status === 'futuro' ? 'Futuro' :
              s.status === 'erro'   ? `${String(s.hora).padStart(2,'0')}h — Offline` :
              s.tempo               ? `${String(s.hora).padStart(2,'0')}h — ${s.tempo}ms` : '—'
            }
            style={{
              flex: 1, minWidth: 0, borderRadius: '2px 2px 0 0',
              background: bg, height: h,
              opacity: s.status === 'futuro' ? 0.3 : 1,
            }}
          />
        );
      })}
    </div>
  );
}

function MiniSparkline({ monitorId }) {
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    const hoje = new Date().toISOString().slice(0, 10);
    api.get(`/monitoramento/${monitorId}/historico?data=${hoje}`)
      .then(r => setSlots(buildTimeline(r.data, hoje)))
      .catch(() => setSlots([]));
  }, [monitorId]);
  if (!slots) return <span style={{ fontSize: 11, color: COR.cinza }}>...</span>;
  return <Sparkline slots={slots} />;
}

// ─── Dropdown ··· (portal — evita ser cortado pelo overflow:hidden da tabela) ──
function MenuAcoes({ ativo, onHistorico, onToggle, onEditar, onExcluir }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      const dentroDoBotao = btnRef.current && btnRef.current.contains(e.target);
      const dentroDoMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!dentroDoBotao && !dentroDoMenu) setOpen(false);
    }
    // 'click' (não 'mousedown') garante que o onClick dos itens do menu
    // dispare primeiro; senão o portal desmonta antes do clique no item ser processado.
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  function abrirMenu() {
    if (open) { setOpen(false); return; }
    const rect = btnRef.current.getBoundingClientRect();
    const menuWidth = 172;
    setPos({
      top: rect.bottom + window.scrollY + 4,
      left: rect.right + window.scrollX - menuWidth,
    });
    setOpen(true);
  }

  const item = (icon, label, onClick, danger) => (
    <button onClick={() => { setOpen(false); onClick(); }} style={{
      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
      padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
      fontSize: 13, color: danger ? COR.vermelho : COR.text, textAlign: 'left',
    }}
      onMouseEnter={e => e.currentTarget.style.background = '#f5f7fa'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >
      <span style={{ fontSize: 15, width: 18 }}>{icon}</span>{label}
    </button>
  );

  const dropdown = open ? ReactDOM.createPortal(
    <div ref={menuRef} style={{
      position: 'absolute', top: pos.top, left: pos.left, zIndex: 9999,
      background: COR.surface, border: `0.5px solid ${COR.border}`,
      borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      minWidth: 172, overflow: 'hidden',
    }}>
      {item('📊', 'Ver histórico', onHistorico)}
      {item(ativo ? '⏸' : '▶', ativo ? 'Pausar' : 'Retomar', onToggle)}
      {item('✏️', 'Editar', onEditar)}
      <div style={{ height: 1, background: COR.border, margin: '2px 0' }} />
      {item('🗑', 'Excluir', onExcluir, true)}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button ref={btnRef} onClick={abrirMenu} style={{
        background: 'none', border: `0.5px solid ${COR.border}`, borderRadius: 6,
        width: 28, height: 28, cursor: 'pointer', fontSize: 17, color: COR.cinza,
        display: 'flex', alignItems: 'center', justifyContent: 'center', letterSpacing: 1,
      }}>···</button>
      {dropdown}
    </>
  );
}

// ─── Modal base ───────────────────────────────────────────
function Modal({ titulo, onClose, children, largura = 640 }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: COR.surface, borderRadius: 14, width: '100%', maxWidth: largura,
        maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: `1px solid ${COR.border}`,
          position: 'sticky', top: 0, background: COR.surface, zIndex: 1,
        }}>
          <h3 style={{ margin: 0, fontSize: 16, color: COR.navy, fontWeight: 700 }}>{titulo}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: COR.cinza }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px' }}>{children}</div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '9px 12px', border: `1.5px solid ${COR.border}`,
  borderRadius: 8, fontSize: 14, color: COR.text, boxSizing: 'border-box',
  background: COR.surface, outline: 'none',
};

function Campo({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 5 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

// ─── Modal Passo ──────────────────────────────────────────
function ModalPasso({ passo, usuarios, categorias, mesas, onSalvar, onClose }) {
  const [form, setForm] = useState({
    acao: passo?.acao || 'criar_ticket',
    incidencias: passo?.incidencias || 4,
    periodo_min: passo?.periodo_min || 30,
    tipo_periodo: passo?.tipo_periodo || 'Minutos',
    categoria_id: passo?.categoria_id || '',
    categoria_nome: passo?.categoria_nome || '',
    subcategoria_id: passo?.subcategoria_id || '',
    subcategoria_nome: passo?.subcategoria_nome || '',
    operador_id: passo?.operador_id || '',
    operador_nome: passo?.operador_nome || '',
    mesa: passo?.mesa || '',
  });
  const [loading, setLoading] = useState(false);
  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  // Valor selecionado no select único de categoria/subcategoria: usa o
  // prefixo "sub-"/"cat-" para diferenciar IDs (categoria e subcategoria
  // usam sequences independentes e podem colidir em valor numérico).
  const valorSelecionado = form.subcategoria_id ? `sub-${form.subcategoria_id}` : (form.categoria_id ? `cat-${form.categoria_id}` : '');

  function selecionarCategoriaOuSub(valor) {
    if (!valor) {
      set('categoria_id', ''); set('categoria_nome', '');
      set('subcategoria_id', ''); set('subcategoria_nome', '');
      return;
    }
    if (valor.startsWith('sub-')) {
      const subId = valor.slice(4);
      // Acha a categoria pai e a subcategoria escolhida para preencher os nomes
      for (const cat of categorias) {
        const sub = (cat.subcategorias || []).find(s => String(s.id) === subId);
        if (sub) {
          set('categoria_id', cat.id); set('categoria_nome', cat.nome);
          set('subcategoria_id', sub.id); set('subcategoria_nome', sub.nome);
          return;
        }
      }
    } else if (valor.startsWith('cat-')) {
      const catId = valor.slice(4);
      const cat = categorias.find(c => String(c.id) === catId);
      set('categoria_id', cat ? cat.id : catId); set('categoria_nome', cat ? cat.nome : '');
      set('subcategoria_id', ''); set('subcategoria_nome', '');
    }
  }

  return (
    <Modal titulo={passo?.id ? 'Editar passo' : 'Novo passo'} onClose={onClose} largura={520}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <Campo label="Ação">
          <select style={inputStyle} value={form.acao} onChange={e => set('acao', e.target.value)}>
            <option value="criar_ticket">Criar ticket</option>
          </select>
        </Campo>
        <Campo label="Incidências do erro">
          <input style={inputStyle} type="number" min={1} value={form.incidencias}
            onChange={e => set('incidencias', parseInt(e.target.value) || 1)} />
        </Campo>
        <Campo label="Período">
          <input style={inputStyle} type="number" min={1} value={form.periodo_min}
            onChange={e => set('periodo_min', parseInt(e.target.value) || 1)} />
        </Campo>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <Campo label="Tipo">
          <select style={inputStyle} value={form.tipo_periodo} onChange={e => set('tipo_periodo', e.target.value)}>
            <option>Minutos</option><option>Horas</option>
          </select>
        </Campo>
        <Campo label="Categorias">
          <select style={inputStyle} value={valorSelecionado}
            onChange={e => selecionarCategoriaOuSub(e.target.value)}>
            <option value="">— Selecione —</option>
            {categorias.map(cat => (
              <optgroup key={cat.id} label={cat.nome}>
                <option value={`cat-${cat.id}`}>{cat.nome}</option>
                {(cat.subcategorias || []).map(sub => (
                  <option key={`sub-${sub.id}`} value={`sub-${sub.id}`}>{sub.nome}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </Campo>
        <Campo label="Operador">
          <select style={inputStyle} value={form.operador_id}
            onChange={e => {
              const opt = e.target.options[e.target.selectedIndex];
              set('operador_id', e.target.value);
              set('operador_nome', e.target.value ? opt.text : '');
            }}>
            <option value="">— Selecione —</option>
            {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </Campo>
      </div>
      <Campo label="Mesa de trabalho">
        <select style={inputStyle} value={form.mesa} onChange={e => set('mesa', e.target.value)}>
          <option value="">— Selecione —</option>
          {mesas.map(m => <option key={m.id} value={m.nome}>{m.nome}</option>)}
        </select>
      </Campo>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button disabled={loading} onClick={async () => { setLoading(true); try { await onSalvar(form); } finally { setLoading(false); } }}
          style={{ background: COR.orange, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          💾 Salvar
        </button>
      </div>
    </Modal>
  );
}

// ─── Modal Monitor ────────────────────────────────────────
function ModalMonitor({ monitor, clientes, usuarios, categorias, mesas, onSalvar, onClose, toast }) {
  const isEdicao = !!monitor?.id;
  const [form, setForm] = useState({
    descricao: monitor?.descricao || '',
    cliente_id: monitor?.cliente_id || '',
    cliente_nome: monitor?.cliente_nome || '',
    unidade_negocio: monitor?.unidade_negocio || '',
    tipo: monitor?.tipo || 'porta',
    host: monitor?.host || '',
    porta: monitor?.porta || '',
    intervalo_min: monitor?.intervalo_min || 5,
  });
  const [passos, setPassos] = useState(monitor?.passos || []);
  const [modalPasso, setModalPasso] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testando, setTestando] = useState(false);
  const [testeResult, setTesteResult] = useState(null);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function testar() {
    if (!monitor?.id) return toast('Salve o monitor primeiro para testar.', 'error');
    setTestando(true); setTesteResult(null);
    try { const r = await api.post(`/monitoramento/${monitor.id}/testar`); setTesteResult(r.data); }
    catch { toast('Erro ao testar conexão', 'error'); }
    setTestando(false);
  }

  async function salvar() {
    if (!form.descricao || !form.host || !form.porta) return toast('Preencha descrição, host e porta', 'error');
    setLoading(true);
    try { await onSalvar(form); onClose(); }
    catch (e) { toast(e?.response?.data?.erro || 'Erro ao salvar', 'error'); }
    setLoading(false);
  }

  async function salvarPasso(dados) {
    try {
      if (modalPasso?.id) { await api.patch(`/monitoramento/passos/${modalPasso.id}`, dados); }
      else if (monitor?.id) { await api.post(`/monitoramento/${monitor.id}/passos`, dados); }
      if (monitor?.id) { const r = await api.get(`/monitoramento/${monitor.id}/passos`); setPassos(r.data); }
      else { setPassos(p => modalPasso?.id ? p.map(x => x._tmp === modalPasso._tmp ? { ...x, ...dados } : x) : [...p, { ...dados, _tmp: Date.now(), ordem: p.length + 1 }]); }
      setModalPasso(null); toast('Passo salvo', 'success');
    } catch { toast('Erro ao salvar passo', 'error'); }
  }

  async function excluirPasso(p) {
    if (!window.confirm('Excluir este passo?')) return;
    try {
      if (p.id) await api.delete(`/monitoramento/passos/${p.id}`);
      setPassos(ps => ps.filter(x => x.id !== p.id && x._tmp !== p._tmp));
      toast('Passo removido', 'success');
    } catch { toast('Erro ao excluir', 'error'); }
  }

  return (
    <Modal titulo={isEdicao ? 'Editar monitoramento de link' : 'Novo monitoramento de link'} onClose={onClose} largura={700}>
      <Campo label="Descrição">
        <input style={inputStyle} value={form.descricao} onChange={e => set('descricao', e.target.value)}
          placeholder="ex: Link principal — Filial Centro" />
      </Campo>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Campo label="Cliente">
          <select style={inputStyle} value={form.cliente_id}
            onChange={e => { const opt = e.target.options[e.target.selectedIndex]; set('cliente_id', e.target.value); set('cliente_nome', e.target.value ? opt.text : ''); }}>
            <option value="">— Selecione —</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </Campo>
        <Campo label="Unidade de Negócio">
          <input style={inputStyle} value={form.unidade_negocio} onChange={e => set('unidade_negocio', e.target.value)} placeholder="Opcional" />
        </Campo>
      </div>
      <Campo label="Tipo de monitoramento">
        <select style={inputStyle} value={form.tipo} onChange={e => set('tipo', e.target.value)}>
          <option value="porta">Porta</option>
        </select>
      </Campo>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px', gap: 12 }}>
        <Campo label="IP (ou URL ou HOST)">
          <input style={inputStyle} value={form.host} onChange={e => set('host', e.target.value)}
            placeholder="ex: 192.168.1.1 ou meucliente.ddns.com.br" />
        </Campo>
        <Campo label="Porta">
          <input style={inputStyle} type="number" value={form.porta} onChange={e => set('porta', e.target.value)} placeholder="ex: 8080" />
        </Campo>
        <Campo label="Intervalo (min)">
          <input style={inputStyle} type="number" min={1} value={form.intervalo_min} onChange={e => set('intervalo_min', parseInt(e.target.value) || 5)} />
        </Campo>
      </div>

      {isEdicao && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button onClick={testar} disabled={testando} style={{ background: 'none', border: `1.5px solid ${COR.orange}`, color: COR.orange, borderRadius: 8, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
            {testando ? '⏳ Testando...' : '🔍 Testar conexão agora'}
          </button>
          {testeResult && (
            <span style={{ fontSize: 13, fontWeight: 600, color: testeResult.ok ? COR.verde : COR.vermelho }}>
              {testeResult.ok ? `✅ Resposta em ${testeResult.tempo}ms` : `❌ ${testeResult.erro}`}
            </span>
          )}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${COR.border}`, paddingTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: 0.6 }}>Passos de ação automática</span>
          <button onClick={() => setModalPasso('novo')} style={{ background: COR.verde, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Adicionar passo</button>
        </div>
        {passos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px 0', color: COR.cinza, fontSize: 13 }}>Nenhum passo configurado. Adicione um para criar tickets automaticamente.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${COR.border}` }}>
                {['Passo', 'Ação', 'Quando', 'Usuário', ''].map((h, i) => (
                  <th key={i} style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7a8d', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {passos.map((p, i) => (
                <tr key={p.id || p._tmp} style={{ borderBottom: `1px solid ${COR.border}` }}>
                  <td style={{ padding: '10px', color: '#6b7a8d' }}>{i + 1}º passo</td>
                  <td style={{ padding: '10px' }}>Criar ticket</td>
                  <td style={{ padding: '10px', color: '#6b7a8d', fontSize: 12 }}>Após {p.incidencias} erros em {p.periodo_min} {p.tipo_periodo}</td>
                  <td style={{ padding: '10px' }}>{p.operador_nome || '—'}</td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setModalPasso(p)} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer' }}>✏️</button>
                      <button onClick={() => excluirPasso(p)} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer' }}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <button disabled={loading} onClick={salvar} style={{ background: COR.orange, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          💾 Salvar
        </button>
      </div>

      {modalPasso && (
        <ModalPasso passo={modalPasso === 'novo' ? null : modalPasso} usuarios={usuarios} categorias={categorias} mesas={mesas} onSalvar={salvarPasso} onClose={() => setModalPasso(null)} />
      )}
    </Modal>
  );
}

// ─── Modal Detalhe ────────────────────────────────────────
function ModalDetalhe({ monitor, onClose }) {
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/monitoramento/${monitor.id}/historico?data=${data}`); setHistorico(r.data); }
    catch {} finally { setLoading(false); }
  }, [monitor.id, data]);

  useEffect(() => { carregar(); }, [carregar]);

  const slots = buildTimeline(historico, data);
  const pings = historico.filter(h => h.status === 'ok' && h.tempo_ms);
  const tempoMedio = pings.length ? Math.round(pings.reduce((a, b) => a + b.tempo_ms, 0) / pings.length) : null;
  const grafPings = historico.filter(h => h.tempo_ms).slice(-30);
  const maxTempo = Math.max(...grafPings.map(h => h.tempo_ms), 1);
  const total = historico.length;
  const oks = historico.filter(h => h.status === 'ok').length;
  const uptime = total > 0 ? Math.round((oks / total) * 100) : null;

  function navData(d) { const dt = new Date(data); dt.setDate(dt.getDate() + d); setData(dt.toISOString().slice(0, 10)); }
  function fmtData(iso) { const [a, m, d] = iso.split('-'); return `${d}/${m}/${a}`; }

  return (
    <Modal titulo="" onClose={onClose} largura={820}>
      <div style={{ marginTop: -4, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: COR.navy }}>{monitor.descricao}</span>
          <StatusPill ativo={monitor.ativo} statusAtual={monitor.status_atual} />
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 13, color: '#6b7a8d', marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {monitor.cliente_nome && <span>🏢 {monitor.cliente_nome}</span>}
          <span>·</span>
          <span style={{ fontFamily: 'monospace', background: '#f1f5f9', padding: '2px 8px', borderRadius: 4 }}>
            {monitor.host}:{monitor.porta}
          </span>
          {uptime !== null && (
            <><span>·</span>
            <span style={{ color: uptime >= 90 ? COR.verde : uptime >= 70 ? COR.amarelo : COR.vermelho, fontWeight: 700 }}>
              {uptime}% uptime hoje
            </span></>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 14 }}>
        <button onClick={() => navData(-1)} style={{ background: 'none', border: `1px solid ${COR.border}`, borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 16 }}>‹</button>
        <span style={{ fontWeight: 700, fontSize: 15, minWidth: 110, textAlign: 'center' }}>{fmtData(data)}</span>
        <button onClick={() => navData(1)} style={{ background: 'none', border: `1px solid ${COR.border}`, borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 16 }}>›</button>
      </div>

      <div style={{ marginBottom: 20 }}>
        <Sparkline slots={slots} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: COR.cinza, marginTop: 4 }}>
          {[0,3,6,9,12,15,18,21,23].map(h => <span key={h}>{String(h).padStart(2,'0')}h</span>)}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: COR.cinza, padding: 40 }}>Carregando...</div>
      ) : grafPings.length > 0 ? (
        <div style={{ background: '#f8fafc', border: `1px solid ${COR.border}`, borderRadius: 10, padding: '16px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Tempo médio: {tempoMedio}ms</span>
            <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#6b7a8d' }}>
              <span>❌ Offline</span><span>✅ Online</span>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <svg width={Math.max(grafPings.length * 28 + 60, 400)} height="160"
              viewBox={`0 0 ${Math.max(grafPings.length * 28 + 60, 400)} 160`}>
              {[0, 100, 200, 300].map(v => {
                const y = 130 - (v / maxTempo) * 100;
                return (
                  <g key={v}>
                    <line x1={45} y1={y} x2={grafPings.length * 28 + 45} y2={y} stroke="#e5e7eb" strokeWidth={1} />
                    <text x={40} y={y + 4} textAnchor="end" fontSize={9} fill="#9ca3af">{v}</text>
                  </g>
                );
              })}
              <polyline fill="none" stroke="#2563eb" strokeWidth={2}
                points={grafPings.map((h, i) => `${45 + i * 28},${130 - (h.tempo_ms / maxTempo) * 100}`).join(' ')} />
              {grafPings.map((h, i) => {
                const x = 45 + i * 28, y = 130 - (h.tempo_ms / maxTempo) * 100;
                return (
                  <g key={i}>
                    <circle cx={x} cy={y} r={4} fill="#fff" stroke={COR.verde} strokeWidth={2} />
                    <text x={x} y={150} textAnchor="middle" fontSize={8} fill="#9ca3af">
                      {new Date(h.criado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: COR.cinza, padding: 40, background: '#f8fafc', borderRadius: 10 }}>
          Nenhum dado para {fmtData(data)}.
        </div>
      )}
    </Modal>
  );
}

// ─── Componente Principal ─────────────────────────────────
export default function Monitoramento({ toast }) {
  const [monitores, setMonitores]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [clientes, setClientes]         = useState([]);
  const [usuarios, setUsuarios]         = useState([]);
  const [categorias, setCategorias]     = useState([]);
  const [mesas, setMesas]               = useState([]);
  const [modalNovo, setModalNovo]       = useState(false);
  const [modalEditar, setModalEditar]   = useState(null);
  const [modalDetalhe, setModalDetalhe] = useState(null);
  const [busca, setBusca]               = useState('');
  const [filtro, setFiltro]             = useState('todos'); // todos | online | erro | pausado
  const [pagina, setPagina]             = useState(1);
  const porPagina = 12;
  const intervalRef = useRef(null);

  const carregar = useCallback(async () => {
    try { const r = await api.get('/monitoramento'); setMonitores(r.data); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    carregar();
    intervalRef.current = setInterval(carregar, 30000);
    return () => clearInterval(intervalRef.current);
  }, [carregar]);

  useEffect(() => {
    api.get('/orcamentos/clientes/todos').then(r => {
      setClientes(Array.isArray(r.data) ? r.data : (r.data?.clientes || r.data?.data || []));
    }).catch(() => {});
    api.get('/usuarios').then(r => {
      const lista = Array.isArray(r.data) ? r.data : (r.data?.usuarios || []);
      setUsuarios(lista.filter(u => u.ativo !== false));
    }).catch(() => {});
    api.get('/tickets/categorias').then(r => setCategorias(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/tickets/mesas').then(r => setMesas(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  async function salvarMonitor(form) { await api.post('/monitoramento', form); carregar(); toast('Monitor criado', 'success'); }
  async function editarMonitor(form) { await api.patch(`/monitoramento/${modalEditar.id}`, form); carregar(); toast('Monitor atualizado', 'success'); }

  async function excluir(id) {
    if (!window.confirm('Excluir este monitor? O histórico também será removido.')) return;
    try { await api.delete(`/monitoramento/${id}`); carregar(); toast('Monitor removido', 'success'); }
    catch { toast('Erro ao excluir', 'error'); }
  }

  async function toggle(id) {
    try { await api.patch(`/monitoramento/${id}/toggle`); carregar(); }
    catch { toast('Erro ao alterar status', 'error'); }
  }

  async function abrirEditar(m) {
    try { const r = await api.get(`/monitoramento/${m.id}`); setModalEditar(r.data); }
    catch { setModalEditar(m); }
  }

  function mudarFiltro(f) { setFiltro(f); setPagina(1); }

  const filtrado = monitores.filter(m => {
    const buscaOk = !busca || [m.descricao, m.cliente_nome, m.host].join(' ').toLowerCase().includes(busca.toLowerCase());
    const filtroOk =
      filtro === 'todos'   ? true :
      filtro === 'online'  ? (m.ativo && m.status_atual === 'ok') :
      filtro === 'erro'    ? (m.ativo && m.status_atual === 'erro') :
      filtro === 'pausado' ? !m.ativo : true;
    return buscaOk && filtroOk;
  });

  const totalPags = Math.ceil(filtrado.length / porPagina);
  const paginaAtual = filtrado.slice((pagina - 1) * porPagina, pagina * porPagina);

  const kpis = [
    { key: 'todos',   label: 'Total',    icon: '🖥', cor: COR.navy,     total: monitores.length },
    { key: 'online',  label: 'Online',   icon: '✅', cor: COR.verde,    total: monitores.filter(m => m.ativo && m.status_atual === 'ok').length },
    { key: 'erro',    label: 'Com erro', icon: '❌', cor: COR.vermelho, total: monitores.filter(m => m.ativo && m.status_atual === 'erro').length },
    { key: 'pausado', label: 'Pausados', icon: '⏸', cor: '#92400e',    total: monitores.filter(m => !m.ativo).length },
  ];

  const thStyle = { padding: '10px 12px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: 0.5 };
  const tdStyle = { padding: '10px 12px', verticalAlign: 'middle', overflow: 'hidden' };

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>

      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, color: COR.navy, fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            🖥 Monitoramento
          </h2>
          <p style={{ margin: '3px 0 0', color: '#6b7a8d', fontSize: 13 }}>Monitoramento de links e equipamentos em tempo real</p>
        </div>
        <button onClick={() => setModalNovo(true)} style={{ background: COR.orange, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + Novo monitor
        </button>
      </div>

      {/* KPIs — filtros */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
        {kpis.map(k => (
          <div key={k.key} onClick={() => mudarFiltro(k.key)} style={{
            background: COR.surface, border: filtro === k.key ? `2px solid ${k.cor}` : `0.5px solid ${COR.border}`,
            borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10,
            cursor: 'pointer', transition: 'border-color .15s',
          }}>
            <div style={{ fontSize: 24, fontWeight: 600, color: k.cor, lineHeight: 1 }}>{k.total}</div>
            <div style={{ fontSize: 11, color: '#6b7a8d' }}>{k.icon} {k.label}</div>
          </div>
        ))}
      </div>

      {/* Busca */}
      <div style={{ marginBottom: 12 }}>
        <input style={{ ...inputStyle, maxWidth: 340 }}
          placeholder="🔍 Buscar por cliente, descrição ou host..."
          value={busca} onChange={e => { setBusca(e.target.value); setPagina(1); }} />
      </div>

      {/* Tabela */}
      <div style={{ background: COR.surface, border: `0.5px solid ${COR.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: COR.cinza }}>Carregando monitores...</div>
        ) : paginaAtual.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: COR.cinza }}>
            {busca || filtro !== 'todos' ? 'Nenhum monitor encontrado.' : 'Nenhum monitor cadastrado.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '34%' }} />
              <col style={{ width: '7%' }} />
            </colgroup>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: `0.5px solid ${COR.border}` }}>
                <th style={thStyle}>Cliente</th>
                <th style={thStyle}>Descrição</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Últimas 24 horas</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {paginaAtual.map((m, idx) => (
                <tr key={m.id} style={{ borderBottom: `0.5px solid ${COR.border}`, background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: COR.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.cliente_nome || <span style={{ color: COR.cinza }}>—</span>}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontSize: 12, color: COR.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.descricao}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <StatusPill ativo={m.ativo} statusAtual={m.status_atual} />
                  </td>
                  <td style={tdStyle}>
                    <MiniSparkline monitorId={m.id} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <MenuAcoes
                      ativo={m.ativo}
                      onHistorico={() => setModalDetalhe(m)}
                      onToggle={() => toggle(m.id)}
                      onEditar={() => abrirEditar(m)}
                      onExcluir={() => excluir(m.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {totalPags > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#6b7a8d' }}>
            Mostrando {(pagina - 1) * porPagina + 1}–{Math.min(pagina * porPagina, filtrado.length)} de {filtrado.length} itens.
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
              style={{ padding: '5px 10px', border: `0.5px solid ${COR.border}`, borderRadius: 6, background: COR.surface, cursor: 'pointer', fontSize: 12 }}>‹</button>
            {Array.from({ length: totalPags }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPagina(p)} style={{
                padding: '5px 10px', border: `0.5px solid ${COR.border}`, borderRadius: 6, fontSize: 12,
                background: p === pagina ? COR.navy : COR.surface,
                color: p === pagina ? '#fff' : COR.text, cursor: 'pointer',
              }}>{p}</button>
            ))}
            <button onClick={() => setPagina(p => Math.min(totalPags, p + 1))} disabled={pagina === totalPags}
              style={{ padding: '5px 10px', border: `0.5px solid ${COR.border}`, borderRadius: 6, background: COR.surface, cursor: 'pointer', fontSize: 12 }}>›</button>
          </div>
        </div>
      )}

      {/* Modais */}
      {modalNovo && (
        <ModalMonitor clientes={clientes} usuarios={usuarios} categorias={categorias} mesas={mesas}
          onSalvar={salvarMonitor} onClose={() => setModalNovo(false)} toast={toast} />
      )}
      {modalEditar && (
        <ModalMonitor monitor={modalEditar} clientes={clientes} usuarios={usuarios} categorias={categorias} mesas={mesas}
          onSalvar={editarMonitor} onClose={() => setModalEditar(null)} toast={toast} />
      )}
      {modalDetalhe && (
        <ModalDetalhe monitor={modalDetalhe} onClose={() => setModalDetalhe(null)} />
      )}
    </div>
  );
}
